import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import i18n from 'i18n';
import QRCode from 'react-qr-code';
import apiCubic from 'config/api-cubic';
import styled from 'styled-components/macro';
import scaleSize from 'utils/scale-size';
import { showToast } from 'actions/toast';
import { getProfileData } from 'reducers/profile/selectors';
import FocusableParent from 'components/navigation/FocusableParent';
import Button from 'components/Button';
import {
  StyledModalContainer,
  StyledTitle,
  StyledDescription,
  StyledBlockWrapper,
  StyledLeftContent,
  StyledButtonWrapper,
  StyledSmallButtonText,
} from '../StyledComponents';
import { showLoader, hideLoader } from 'actions/loader';
import useCreateLifecellPaymentLink from 'hooks/useCreateLifecellPaymentLink';
import { resetUserData } from 'actions/app';

const TIME_TO_START_INTERVAL = 30 * 1000;
// 5, 10 & 30 seconds
const INTERVALS_MS = [5 * 1000, 10 * 1000, 30 * 1000];

const ExtendedBlockWrapper = styled(StyledBlockWrapper)`
  padding: ${scaleSize(60)}px ${scaleSize(80)}px;
`;

const ExtendedTitle = styled(StyledTitle)`
  min-height: ${scaleSize(60)}px;
  padding: 0 ${scaleSize(15)}px ${scaleSize(40)}px 0;
  margin: 0;
  font-size: ${scaleSize(38)}px;
  text-align: left;
  text-overflow: ellipsis;
  text-wrap: nowrap;
  overflow: hidden;
`;

const ExtendedDescription = styled(StyledDescription)`
  height: auto;
  padding-bottom: ${scaleSize(30)}px;
  text-align: left;

  p {
    font-size: ${scaleSize(30)}px;
  }
`;

const StyledRightContent = styled.div`
  margin-top: ${scaleSize(55)}px;
  width: 40%;
  float: right;
  text-align: center;
`;

const LifecellPaymentModal = ({
  title = i18n.t('packages.title'),
  description,
  id,
  assetType,
  onModalConfirm,
  onModalBack,
}) => {
  const dispatch = useDispatch();
  const openTimestampRef = useRef(moment(Date.now()));
  const { customerRef, contractNumber } = useSelector(getProfileData);
  const [isIntervalStarted, setIsIntervalStarted] = useState(false);
  const [currentIntervalMs, setCurrentIntervalMs] = useState(INTERVALS_MS[0]);

  const { isLoading, link } = useCreateLifecellPaymentLink({
    contractCode: contractNumber,
    customerRef,
    id,
    assetType,
  });

  const checkLastActivation = useCallback(async () => {
    const { data } = await apiCubic.post({
      endpoint: apiCubic.endpoints.checkLastServiceActivation,
      data: { contractCode: contractNumber },
    });

    const lastActivation = data?.payload?.lastActivation;
    const isBought = moment(lastActivation) > openTimestampRef.current;

    if (isBought) {
      // need to wait for this one
      await dispatch(resetUserData());

      dispatch(showToast({ options: { toastText: i18n.t('common.toast.successPurchase') } }));

      if (onModalConfirm) onModalConfirm();
    }
  }, [contractNumber, dispatch, onModalConfirm]);

  useEffect(() => {
    dispatch(isLoading ? showLoader() : hideLoader());
  }, [dispatch, isLoading]);

  useEffect(() => {
    const timerId = setTimeout(() => setIsIntervalStarted(true), TIME_TO_START_INTERVAL);
    return () => clearTimeout(timerId);
  }, []);

  useEffect(() => {
    if (!isIntervalStarted) return;

    checkLastActivation();

    const intervalId = setInterval(() => {
      const isThreeMinutesPassed = Date.now() - openTimestampRef.current - TIME_TO_START_INTERVAL > 3 * 60 * 1000;
      const isFiveMinutesPassed = Date.now() - openTimestampRef.current - TIME_TO_START_INTERVAL > 5 * 60 * 1000;

      // Every 5 seconds for first 3 minutes
      // Every 10 for nexdt 2 minutes
      // Every 30 seconds for rest
      if (isFiveMinutesPassed) {
        setCurrentIntervalMs(INTERVALS_MS[2]);
      } else if (isThreeMinutesPassed) {
        setCurrentIntervalMs(INTERVALS_MS[1]);
      } else {
        setCurrentIntervalMs(INTERVALS_MS[0]);
      }
      checkLastActivation();
    }, currentIntervalMs);

    return () => clearInterval(intervalId);
  }, [isIntervalStarted, currentIntervalMs, checkLastActivation]);

  if (isLoading) return null;

  return (
    <StyledModalContainer>
      <ExtendedBlockWrapper>
        <StyledLeftContent>
          <ExtendedTitle>{title}</ExtendedTitle>
          <ExtendedDescription>
            {description || (
              <>
                <p>{i18n.t('profile.packagesModalTitle')}</p>
                <p>{i18n.t('profile.packagesModalDescription')}</p>
                <p>{i18n.t('common.modal.lifeModalDescription')}</p>
              </>
            )}
          </ExtendedDescription>
          <FocusableParent
            config={{
              leaveFor: {
                right: '',
                up: '',
                left: '',
                down: '',
              },
            }}
            defaultSection
          >
            <StyledButtonWrapper>
              <Button onEnter={onModalBack} focusOnMouseEnter>
                <StyledSmallButtonText>{i18n.t('common.cancel')}</StyledSmallButtonText>
              </Button>
            </StyledButtonWrapper>
          </FocusableParent>
        </StyledLeftContent>
        <StyledRightContent>{link && <QRCode value={link} size={scaleSize(450)} />}</StyledRightContent>
      </ExtendedBlockWrapper>
    </StyledModalContainer>
  );
};

export default LifecellPaymentModal;
